import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
} from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { CircularProgress, Typography } from '@mui/material';
import {
  Breadcrumbs,
  Button,
  Flex,
  FlexColumn,
  IconButton,
  LoadingButton,
  SvgIcon,
} from '@elementary/common-ui/components';
import { ReactComponent as ArrowLeft } from '@elementary/common-ui/assets/arrow-left.svg';
import { ReactComponent as MergeIcon } from '@elementary/common-ui/assets/20x20/merge.svg';
import { invalidateEntityQueries } from '@elementary/common-ui/utils/queryInvalidation';
import {
  INCIDENT_ID,
  MERGE_INCIDENT_IDS_PARAM,
} from '@elementary/common-ui/constants/urlParams';
import { agentMergeIncidentsPlan } from '@elementary/common-ui/atoms';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import useToggleTreeView from '@elementary/common-ui/hooks/useToggleTreeView';
import useSnackbar from 'shared/hooks/useSnackbar';
import useNavigateWithEnv from 'shared/hooks/useNavigateWithEnv';
import useUrlWithEnv from 'shared/hooks/useUrlWithEnv';
import { useRecoilState } from 'recoil';
import { space } from '@elementary/common-ui/style/utils';
import { mergeIncidents } from '../api';
import useIncidentMetadata from '../hooks/useIncidentMetadata';
import useIncidentMergeCards from '../hooks/useIncidentMergeCards';
import MergedIncidentPanel from './MergedIncidentPanel';
import MergeIncidentSelector from './MergeIncidentSelector';

const PageRoot = styled(FlexColumn)`
  height: 100%;
  min-width: 930px;
  overflow: hidden;
`;

const HeaderRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${space('md')};
  padding: ${space('xl', 'xxxl')};
  border-bottom: 1px solid ${({ theme }) => theme.palette.border.subtle};
  flex: 0 0 auto;
`;

const HeaderNavigation = styled(Flex)`
  align-items: center;
  gap: ${space('lg')};
  min-width: 0;
  flex: 1;
`;

const ContentRow = styled(Flex)`
  align-items: stretch;
  gap: 0;
  min-height: 0;
  min-width: 0;
  flex: 1;
  overflow: hidden;
`;

const ContentDivider = styled.div`
  align-self: stretch;
  width: 1px;
  flex: 0 0 auto;
  background: ${({ theme }) => theme.palette.surface.hover};
`;

const LoadingState = styled(Flex)`
  flex: 1;
  min-height: 200px;
  align-items: center;
  justify-content: center;
`;

const BackButton = styled(IconButton)`
  svg path {
    fill: ${({ theme }) => theme.palette.icon.faint} !important;
  }

  &:hover svg path {
    fill: ${({ theme }) => theme.palette.icon.primary} !important;
  }
`;

const parseIncidentIds = (raw: string | null): string[] => {
  if (!raw || !raw.trim()) return [];
  return [
    ...new Set(
      raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  ];
};

const MergeIncidentsPage = () => {
  const envId = useSelectedEnv();
  const navigate = useNavigateWithEnv();
  const navigateBack = useNavigate();
  const getUrlWithEnv = useUrlWithEnv();
  const { openSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const hasPermission = useEnvPermission();
  const canEdit = hasPermission('EDIT_INCIDENTS');
  const { setTreeViewCollapsed } = useToggleTreeView({
    restoreOnUnmount: true,
  });

  const { usersData } = useIncidentMetadata();

  const incidentIds = useMemo(
    () => parseIncidentIds(searchParams.get(MERGE_INCIDENT_IDS_PARAM)),
    [searchParams],
  );

  const {
    data: incidents = [],
    isLoading,
    isError,
  } = useIncidentMergeCards(incidentIds);

  const [mergePlan, setMergePlan] = useRecoilState(agentMergeIncidentsPlan);

  const [primaryId, setPrimaryId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [userSummary, setUserSummary] = useState('');
  const hasNavigatedAfterAgentMergeRef = useRef(false);

  useEffect(() => {
    setTreeViewCollapsed(true);
  }, [setTreeViewCollapsed]);

  const isAgentMode = Boolean(
    mergePlan?.toolCallId && !mergePlan.agentAcknowledged,
  );
  const isPendingAgentApproval =
    mergePlan?.userResponse === 'confirmed' && !mergePlan?.agentAcknowledged;

  useEffect(() => {
    const needsPrimarySelection =
      incidents.length > 0 &&
      !incidents.some((incident) => incident.id === primaryId);
    if (!needsPrimarySelection) return;

    const hasPendingAgentMergePlan =
      !!mergePlan?.primaryIncidentId &&
      !!mergePlan.toolCallId &&
      !mergePlan.agentAcknowledged;

    const plannedPrimary = hasPendingAgentMergePlan
      ? incidents.find(
          (incident) => incident.id === mergePlan.primaryIncidentId,
        )
      : null;

    const nextPrimary = plannedPrimary ?? incidents[0];
    if (!nextPrimary) return;

    setPrimaryId(nextPrimary.id);
    setTitle(
      plannedPrimary && mergePlan?.title ? mergePlan.title : nextPrimary.title,
    );
    setUserSummary(
      plannedPrimary && mergePlan?.userSummary ? mergePlan.userSummary : '',
    );
  }, [incidents, primaryId, mergePlan]);

  useEffect(() => {
    const shouldNavigateAfterAgentApproval =
      !hasNavigatedAfterAgentMergeRef.current &&
      mergePlan?.agentAcknowledged === true &&
      mergePlan.userResponse === 'confirmed';

    if (!shouldNavigateAfterAgentApproval) {
      return;
    }
    hasNavigatedAfterAgentMergeRef.current = true;
    navigate('/incidents', { replace: true });
  }, [mergePlan?.agentAcknowledged, mergePlan?.userResponse, navigate]);

  const onPrimaryChange = useCallback(
    (id: string) => {
      setPrimaryId(id);
      const inc = incidents.find((i) => i.id === id);
      if (inc) {
        setTitle(inc.title);
      }
    },
    [incidents],
  );

  const primaryForPanel = useMemo(() => {
    if (!primaryId) return null;
    return incidents.find((i) => i.id === primaryId) ?? null;
  }, [incidents, primaryId]);

  const { mutateAsync: runMerge, isLoading: isMerging } = useMutation({
    mutationFn: async () => {
      if (!envId || !primaryId) throw new Error('Missing context');
      const sourceIds = incidentIds.filter((id) => id !== primaryId);
      if (sourceIds.length === 0) throw new Error('No source incidents');
      return mergeIncidents(
        envId,
        primaryId,
        sourceIds,
        title.trim() || undefined,
        userSummary.trim() || undefined,
      );
    },
    onSuccess: (response) => {
      void invalidateEntityQueries(queryClient, 'incidents');
      openSnackbar({ variant: 'success', message: 'Incidents merged' });
      navigate(`/incidents?${INCIDENT_ID}=${response.id}`, {
        replace: true,
      });
    },
    onError: () => {
      openSnackbar({ variant: 'error', message: 'Merge failed' });
    },
  });

  const handleMergeClick = useCallback(() => {
    if (isAgentMode && mergePlan?.toolCallId) {
      if (!primaryId) return;
      const sourceIds = incidentIds.filter((id) => id !== primaryId);
      if (sourceIds.length === 0) return;
      setMergePlan((prev) => {
        if (!prev?.toolCallId || prev.agentAcknowledged) return prev;
        return {
          ...prev,
          primaryIncidentId: primaryId,
          sourceIncidentIds: sourceIds,
          title: title.trim() || undefined,
          userSummary: userSummary.trim() || undefined,
          userResponse: 'confirmed',
        };
      });
      return;
    }
    void runMerge();
  }, [
    incidentIds,
    isAgentMode,
    mergePlan?.toolCallId,
    primaryId,
    runMerge,
    setMergePlan,
    title,
    userSummary,
  ]);

  const onBack = useCallback(() => {
    navigateBack(-1);
  }, [navigateBack]);

  const n = incidentIds.length;
  const incidentsPath = getUrlWithEnv('/incidents');

  const breadcrumbItems = useMemo(
    () => [
      'Response & Triage',
      {
        label: 'Incidents',
        onClick: (e: MouseEvent<HTMLAnchorElement>) => {
          e.preventDefault();
          navigate('/incidents');
        },
      },
      `Merge ${n} Incidents`,
    ],
    [n, navigate],
  );

  if (!canEdit) {
    return <Navigate to={incidentsPath} replace />;
  }

  if (n < 2) {
    return <Navigate to={incidentsPath} replace />;
  }

  if (isError) {
    return (
      <PageRoot>
        <Typography color="error">
          Could not load incidents. Try again from the list.
        </Typography>
        <Button variant="outlined" onClick={onBack}>
          Back to incidents
        </Button>
      </PageRoot>
    );
  }

  if (!isLoading && n >= 2 && incidents.length < 2) {
    return <Navigate to={incidentsPath} replace />;
  }

  if (isLoading || !primaryForPanel) {
    return (
      <PageRoot>
        <LoadingState>
          <CircularProgress />
        </LoadingState>
      </PageRoot>
    );
  }

  return (
    <PageRoot>
      <HeaderRow>
        <HeaderNavigation>
          <BackButton
            onClick={onBack}
            aria-label="Back to incidents"
            size="small"
            sx={{ p: '4px' }}
          >
            <SvgIcon width={16} height={14} fillColor="icon.faint">
              <ArrowLeft />
            </SvgIcon>
          </BackButton>
          <Breadcrumbs items={breadcrumbItems} showTreeButton={false} />
        </HeaderNavigation>
        <LoadingButton
          loading={isMerging || isPendingAgentApproval}
          onClick={handleMergeClick}
          startIcon={
            <SvgIcon fillColor="icon.inverse">
              <MergeIcon />
            </SvgIcon>
          }
        >
          Merge {n} Incidents
        </LoadingButton>
      </HeaderRow>

      <ContentRow>
        <MergeIncidentSelector
          incidents={incidents}
          primaryId={primaryId!}
          onPrimaryChange={onPrimaryChange}
        />
        <ContentDivider />
        <MergedIncidentPanel
          title={title}
          userSummary={userSummary}
          onTitleChange={setTitle}
          onUserSummaryChange={setUserSummary}
          primaryIncident={primaryForPanel}
          usersData={usersData}
          disabled={isMerging || isPendingAgentApproval}
        />
      </ContentRow>
    </PageRoot>
  );
};

export default MergeIncidentsPage;
